import React, { useEffect, useState } from 'react'
import { Alert, Col, Container, Form, Row } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  useDeleteUserShareSettingsMutation,
  useGetUserByIdQuery,
  useGetUserShareSettingsQuery,
  useGetAllUsersQuery,
  useSaveUserByIdMutation,
  useSaveUserShareSettingsMutation,
} from '../../store/user/user'
import { DEFAULT_SESSION_SHARING_ENABLED, useGetAccountUserSettingsQuery } from '../../store/accountUserSettings/accountUserSettings'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import PrizeBadge from '../../components/ScopedBlock/PrizeBadge.svg?url'
import { Button } from '../../components/Button/Button'
import { useNavigate } from 'react-router-dom'

import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ShareType, User } from '../../models/User'
import { MultiSelect, Option } from 'react-multi-select-component'
import './Collaboration.scss'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { addToast } from '../../store/toast/toastSlice'
import { TagElement } from '../../components/Tag/Tag'

interface UserShareFormProps {
  shareType: ShareType
  attachedShareTypes?: ShareType[]
  noSelectedUserText: string
  infoText?: string
  selectText?: string
  saveButtonText?: string
  multiSelectWarningText?: string
  hideSaveButton?: boolean
  onSelectedUsersChange?: (userIds: string[]) => void
  userId: string
  showInModal?: boolean
}

export const UserShareForm: React.FC<UserShareFormProps> = ({
  shareType,
  attachedShareTypes = [],
  noSelectedUserText,
  infoText,
  selectText,
  saveButtonText,
  multiSelectWarningText,
  hideSaveButton,
  onSelectedUsersChange,
  userId,
  showInModal,
}) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const { data: users, isFetching: usersFetching } = useGetAllUsersQuery({
    accountId: accountId,
    identityTypes: ['idp', 'cross-account'],
  })

  const { data: userShareSettings, isFetching: shareSettingsFetching } = useGetUserShareSettingsQuery({
    accountId: accountId,
    userId,
    shareType,
    pageRequest: {
      page: 0,
      size: 500,
    },
  })

  const [saveUserShareSettings] = useSaveUserShareSettingsMutation()

  const [userOptions, setUserOptions] = useState<UserOption[]>([])
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([])
  const resolveUserName = (user: User) => {
    const first_last = [user.firstName, user.lastName].filter((i) => i).join(' ')
    const name = user.name
    return name && name !== user.email ? name : first_last || user.email
  }

  useEffect(() => {
    const mapToUserOption = (user: User) => {
      return { value: user.id, key: user.id, label: `${resolveUserName(user)}`, email: user.email, crossAccount: user.type === 'cross-account' }
    }

    if (users && userShareSettings) {
      setUserOptions(users.data.filter((user) => !!user.email && user.id !== userId).map(mapToUserOption))
      const selected = userShareSettings.data ?? []
      const initialSelected = users.data.filter((u) => selected.map((s) => s.sharedWithUserId).includes(u.id)).map(mapToUserOption)
      setSelectedUsers(initialSelected)
      onSelectedUsersChange?.(initialSelected.map((u) => u.value))
    }
  }, [users, userShareSettings, userId, onSelectedUsersChange])

  const customValueRenderer = (selected: Option[], _options: Option[]) => {
    return selected.length ? `${selected.length} user${selected.length > 1 ? 's' : ''} selected` : noSelectedUserText
  }

  const ArrowRenderer = ({ expanded }: { expanded: boolean }) => (
    <>{expanded ? <i className='bi bi-chevron-contract' /> : <i className='bi bi-chevron-expand' />}</>
  )

  const ClearIcon = <i className='bi bi-x-lg' />

  const handleSaveUserShareSettings = async () => {
    await saveUserShareSettings({
      accountId,
      userId,
      payload: {
        shareType,
        sharedWithUserIds: selectedUsers.map((u) => u.value),
      },
    })
    for (const shareType of attachedShareTypes) {
      await saveUserShareSettings({
        accountId,
        userId,
        payload: {
          shareType,
          sharedWithUserIds: selectedUsers.map((u) => u.value),
        },
      })
    }
    dispatch(addToast({ variant: 'success', title: 'Sharing settings saved', messages: ['Sharing settings saved successfully.'] }))
  }

  interface UserOption extends Option {
    email: string
    crossAccount: boolean
  }

  interface CheckboxItemRendererProps {
    checked: boolean
    option: UserOption
    disabled?: boolean
    onClick: () => void
  }

  const CheckboxItemRenderer = ({ checked, option, onClick, disabled }: CheckboxItemRendererProps) => {
    return (
      <div className={`item-renderer ${disabled ? 'disabled' : ''}`}>
        <Form.Check
          disabled={disabled || false}
          key={option.key}
          type='checkbox'
          id={`option_${option.key}`}
          label={
            <>
              {option.value !== '' ? (
                <>
                  <strong>{option.label}</strong> - <small>{option.email}</small>
                  {option.crossAccount ? <TagElement className='user-share-user-tag ms-2'>CROSS-ACCOUNT</TagElement> : null}
                </>
              ) : (
                option.label
              )}
            </>
          }
          checked={checked}
          onChange={onClick}
          value={option.value}
        />
      </div>
    )
  }

  return (
    <>
      {infoText ? (
        <Row>
          <Col>
            <Alert variant='warning' className='d-flex gap-3 align-items-center'>
              <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
              <span className='zocks-text-gray-900'>{infoText}</span>
            </Alert>
          </Col>
        </Row>
      ) : null}
      <Row className={`${showInModal ? 'flex-column' : ''}`}>
        {selectText && <Col>{selectText}</Col>}
        <Col>
          <MultiSelect
            hasSelectAll={false}
            options={userOptions}
            isLoading={usersFetching || shareSettingsFetching}
            value={selectedUsers}
            onChange={(selected: UserOption[]) => {
              setSelectedUsers(selected)
              onSelectedUsersChange?.(selected.map((u) => u.value))
            }}
            labelledBy={'Select'}
            isCreatable={true}
            valueRenderer={customValueRenderer}
            ItemRenderer={CheckboxItemRenderer}
            ArrowRenderer={ArrowRenderer}
            ClearSelectedIcon={ClearIcon}
            overrideStrings={{ noOptions: 'You do not have any teammates.', selectAll: 'Select/Deselect All' }}
          />
        </Col>
        {multiSelectWarningText && !usersFetching && selectedUsers.length === 0 && (
          <Col className='d-flex gap-1 align-items-center'>
            <i className='bi bi-exclamation-triangle zocks-text-warning-text' />
            <span className='multi-select-warning-text'>{multiSelectWarningText}</span>
          </Col>
        )}
        {showInModal && selectedUsers.length > 0 ? (
          <Col className='d-flex flex-wrap gap-2 mt-2'>
            {selectedUsers.map((user) => (
              <span key={user.value} className='d-flex align-items-center gap-2 rounded-1 p-2 zocks-bg-color-gray-200 zocks-text-gray-900'>
                {user.label}
                <i
                  className='bi bi-x cursor-pointer'
                  role='button'
                  onClick={() => {
                    const updated = selectedUsers.filter((u) => u.value !== user.value)
                    setSelectedUsers(updated)
                    onSelectedUsersChange?.(updated.map((u) => u.value))
                  }}
                />
              </span>
            ))}
          </Col>
        ) : null}
      </Row>

      {!hideSaveButton ? (
        <Row>
          <Col>
            <div className='d-flex justify-content-sm-center justify-content-md-end'>
              <Button className='p-2' variant='primary' onClick={handleSaveUserShareSettings}>
                {saveButtonText}
              </Button>
            </div>
          </Col>
        </Row>
      ) : null}
    </>
  )
}

export const CollaborationTab: React.FC = () => {
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const { hasQuota } = useZocksQuota()
  const sessionSharingAvailable = hasQuota('identity.sessionSharingEnabled.value')

  const { data: accountUserSettings, isFetching: accountUserSettingsLoading } = useGetAccountUserSettingsQuery(
    { accountId },
    { refetchOnMountOrArgChange: true, skip: !sessionSharingAvailable },
  )

  const { data: userData, isFetching: userLoading } = useGetUserByIdQuery(
    { accountId, userId: currentUserId },
    { refetchOnMountOrArgChange: true, skip: !sessionSharingAvailable },
  )

  const [saveUserById, { isLoading: isUserUpdating }] = useSaveUserByIdMutation()
  const [deleteUserShareSettings] = useDeleteUserShareSettingsMutation()

  const [sharingEnabled, setSharingEnabled] = useState<boolean>(true)

  useEffect(() => {
    if (!userData || !accountUserSettings) {
      return
    }
    const userSetting = userData.data.settings
    const isUserSharingEnabled = !!(userSetting && userSetting.contactSharingEnabled && userSetting.sessionSharingEnabled)
    const isAccountSharingEnabled = !!(
      accountUserSettings.data &&
      accountUserSettings.data.settingsOverride &&
      accountUserSettings.data.settingsOverride.sessionSharingEnabled &&
      accountUserSettings.data.settingsOverride.contactSharingEnabled
    )

    if (userSetting) {
      setSharingEnabled(isUserSharingEnabled)
    } else if (accountUserSettings.data?.settingsOverride) {
      setSharingEnabled(isAccountSharingEnabled)
    } else {
      setSharingEnabled(DEFAULT_SESSION_SHARING_ENABLED)
    }
  }, [userData, accountUserSettings])

  const handleUpgradePlanClick = () => {
    navigate('/account/plan/plan')
  }

  if (userLoading || accountUserSettingsLoading) {
    return <div className='skeleton' style={{ width: '100%', height: '24px' }} />
  }

  if (!sessionSharingAvailable) {
    return (
      <Container className='p-0'>
        <Row className='zocks-mb-2.5 collaboration-disabled-container gap-5'>
          <Col xs={12} lg>
            <div className='d-flex flex-column gap-3'>
              <h5>Upgrade to unlock this feature</h5>
              <div className='d-flex flex-column gap-2'>
                <div className='d-flex gap-3 align-items-center'>
                  <i className='bi bi-check-circle-fill' />
                  <div className='label-medium'>Collaborate with your team members</div>
                </div>
                <div className='d-flex gap-3 align-items-center'>
                  <i className='bi bi-check-circle-fill' />
                  <div className='label-medium'>Control who can discover your meetings</div>
                </div>
              </div>
            </div>
          </Col>
          <Col xs={12} lg className='d-flex flex-column justify-content-center align-items-lg-end'>
            <div className='d-flex'>
              <Button variant='primary' onClick={handleUpgradePlanClick}>
                Upgrade plan <img alt='Upgrade' src={PrizeBadge} />
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    )
  }

  const handleSessionSharingEnabledChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!userData) {
      return
    }
    const currentUserData = userData.data
    setSharingEnabled(e.currentTarget.checked)
    saveUserById({
      accountId,
      userId: currentUserId,
      userData: { settings: { ...currentUserData.settings, sessionSharingEnabled: e.currentTarget.checked, contactSharingEnabled: e.currentTarget.checked } },
    })
    if (!e.currentTarget.checked) {
      deleteUserShareSettings({ accountId, userId: currentUserId, shareType: 'meeting' })
      deleteUserShareSettings({ accountId, userId: currentUserId, shareType: 'contact' })
    }
  }

  return (
    <Container className='d-flex flex-column gap-5 p-0'>
      <Row>
        <Col>
          <h5>Share meetings and contacts with your team members</h5>
        </Col>
      </Row>
      <Row className='gap-3 p-0 align-items-center'>
        <Col>
          <ScopedBlock
            scope={{
              scope: 'user:update',
              superScope: ['user:admin'],
              resource: `user:${currentUserId}`,
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Row>
              <Col className='paragraph-medium'>I want to share my meetings and contacts with my team members</Col>
              <Col>
                <Form.Switch
                  disabled={userLoading || accountUserSettingsLoading || isUserUpdating}
                  checked={sharingEnabled}
                  onChange={(e) => handleSessionSharingEnabledChanged(e)}
                />
              </Col>
            </Row>
          </ScopedBlock>
        </Col>
      </Row>
      {sharingEnabled && (
        <UserShareForm
          shareType={'meeting'}
          attachedShareTypes={['contact']}
          noSelectedUserText={'All users can discover your meetings and contacts'}
          infoText={
            'You can check each user to grant individual access to your meetings and contacts. If all checkboxes remain unchecked, they will be shared with all teammates.'
          }
          selectText={'Specify access to your meetings and contacts for selected users.'}
          saveButtonText={'Save meeting and contact share settings'}
          userId={currentUserId}
        />
      )}
    </Container>
  )
}
